import { NotFoundError } from "@core/foundation/error";
import { findIntersectionKeys } from "@core/foundation/object";
import type { Promisable } from "@core/foundation/promise";
import { singleline } from "@core/foundation/string";
import { isNullish } from "@core/foundation/types";
import type { ZodObject, ZodReadonly } from "@core/zod";

import { JsonDuplicateError } from "./JsonDuplicateError.ts";
import type {
  JsonIdentifier,
  JsonIdentifierInput,
  JsonPayload,
  JsonPayloadInput,
  JsonRepositoryContract,
  JsonUpdate,
  JsonUpdateOptions,
  JsonUpdateSchema,
} from "./JsonRepositoryContract.ts";
import type {
  JsonCreate,
  JsonRepositoryHelperContract,
  PK,
  SK,
  SK2,
} from "./JsonRepositoryHelperContract.ts";
import { ETAG_FIELD } from "./JsonRepositoryHelperContract.ts";
import type { JsonTransactionContract } from "./JsonTransactionContract.ts";

export abstract class BaseJsonRepository<
  Identifier extends ZodObject,
  Data extends ZodObject,
  Payload extends JsonPayload<Identifier, Data>,
  Transaction extends JsonTransactionContract,
  EntityType extends string,
> implements JsonRepositoryContract<Identifier, Data, Payload, Transaction>
{
  public readonly __typeTag = "JsonRepositoryContract";

  public readonly entityType: EntityType;

  public readonly resourceName: string;

  protected readonly dataSchema: ZodReadonly<Data>;

  protected readonly db: JsonRepositoryHelperContract<
    Identifier,
    Data,
    Transaction,
    EntityType
  >;

  protected readonly identifierSchema: ZodReadonly<Identifier>;

  protected readonly updateSchema: JsonUpdateSchema<Data>;

  protected constructor({
    dataSchema,
    db,
    entityType,
    identifierSchema,
    resourceName,
  }: {
    readonly dataSchema: ZodReadonly<Data>;
    readonly db: JsonRepositoryHelperContract<
      Identifier,
      Data,
      Transaction,
      EntityType
    >;
    readonly entityType: EntityType;
    readonly identifierSchema: ZodReadonly<Identifier>;
    readonly resourceName: string;
  }) {
    const overlapKeys = findIntersectionKeys(
      identifierSchema.shape,
      dataSchema.shape,
    );

    if (overlapKeys.length > 0) {
      throw new Error(
        singleline`
          Identifier and data schemas overlap on keys: ${overlapKeys.join(", ")}
        `,
      );
    }

    const writableSchema = Object.hasOwn(dataSchema.shape, ETAG_FIELD)
      ? dataSchema.omit({ [ETAG_FIELD]: true })
      : dataSchema;

    this.dataSchema = dataSchema;
    this.db = db;
    this.entityType = entityType;
    this.identifierSchema = identifierSchema;
    this.resourceName = resourceName;

    this.updateSchema =
      // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
      writableSchema.partial() as unknown as JsonUpdateSchema<Data>;
  }

  /**
   * @throws {JsonDuplicateError} On duplicate.
   */
  public create(
    payload: JsonPayloadInput<Identifier, Data>,
  ): Promisable<Payload> {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return this.db.create(this.decodePayload(payload)) as Promisable<Payload>;
  }

  public async findById(
    identifier: JsonIdentifierInput<Identifier>,
  ): Promise<Payload | null> {
    const safeIdentifier = this.identifierSchema.decode(identifier);

    const PK = this.getPK(safeIdentifier);

    const SK = this.getSK(safeIdentifier);

    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return this.db.getById(PK, SK) as Promisable<Payload | null>;
  }

  /**
   * @throws {NotFoundError} On a missing record.
   */
  public async getById(
    identifier: JsonIdentifierInput<Identifier>,
  ): Promise<Payload> {
    const payload = await this.findById(identifier);

    if (isNullish(payload)) {
      throw this.createNotFoundError(identifier);
    }

    return payload;
  }

  public async getByIds(
    identifiers: readonly JsonIdentifierInput<Identifier>[],
  ): Promise<readonly Payload[]> {
    const keys = identifiers.map(identifier => {
      const safeIdentifier = this.identifierSchema.decode(identifier);

      const PK = this.getPK(safeIdentifier);

      const SK = this.getSK(safeIdentifier);

      return { PK, SK };
    });

    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return this.db.getByIds(keys) as Promisable<readonly Payload[]>;
  }

  public async getOrCreate(
    identifier: JsonIdentifierInput<Identifier>,
    payloadFactory: () => JsonPayloadInput<Identifier, Data>,
  ): Promise<Payload> {
    try {
      let payload = await this.findById(identifier);

      payload ??= await this.create(payloadFactory());

      return payload;
    } catch (error) {
      if (error instanceof JsonDuplicateError) {
        return this.getById(identifier);
      }

      throw error;
    }
  }

  public async prepareCreate(
    transaction: Transaction,
    payload: JsonPayloadInput<Identifier, Data>,
  ): Promise<void> {
    await this.db.prepareCreate(transaction, this.decodePayload(payload));
  }

  public async prepareUpdate(
    transaction: Transaction,
    payload: Payload,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<void> {
    await this.doPrepareUpdate(transaction, payload, update, options);
  }

  public async prepareUpdateById(
    transaction: Transaction,
    identifier: JsonIdentifierInput<Identifier>,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<void> {
    await this.doPrepareUpdate(
      transaction,
      this.identifierSchema.decode(identifier),
      update,
      options,
    );
  }

  /**
   * @throws {NotFoundError} On a missing record.
   * @throws {JsonWriteConflictError} If the stored etag differs from the
   *   one the options name.
   */
  public async update(
    payload: Payload,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<Payload> {
    return this.doUpdate(payload, update, options);
  }

  /**
   * @throws {NotFoundError} On a missing record.
   * @throws {JsonWriteConflictError} If the stored etag differs from the
   *   one the options name.
   */
  public async updateById(
    identifier: JsonIdentifierInput<Identifier>,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<Payload> {
    return this.doUpdate(
      this.identifierSchema.decode(identifier),
      update,
      options,
    );
  }

  public async upsert(
    payload: JsonPayloadInput<Identifier, Data>,
    updateFactory: () => Promisable<JsonUpdate<Data>>,
  ): Promise<Payload> {
    try {
      return await this.create(payload);
    } catch (error) {
      if (error instanceof JsonDuplicateError) {
        const update = await updateFactory();

        return this.updateById(payload, update);
      }

      throw error;
    }
  }

  protected abstract getPK(
    identifier: JsonIdentifier<Identifier>,
  ): PK<EntityType>;

  protected abstract getSK(identifier: JsonIdentifier<Identifier>): SK;

  protected getSK2(_payload: JsonPayload<Identifier, Data>): SK2 | undefined {
    return undefined;
  }

  private createNotFoundError(
    identifier: JsonIdentifier<Identifier> | JsonIdentifierInput<Identifier>,
  ): NotFoundError {
    return new NotFoundError({
      message: `${this.resourceName} not found: ${JSON.stringify(identifier)}`,
      userPresentableMessage: `${this.resourceName} not found.`,
    });
  }

  private decodePayload(
    payload: JsonPayloadInput<Identifier, Data>,
  ): JsonCreate<Identifier, Data, EntityType> {
    const safeIdentifier = this.identifierSchema.decode(payload);

    const safeData = this.dataSchema.decode(payload);
    const safePayload = { ...safeIdentifier, ...safeData } as const;

    return {
      PK: this.getPK(safeIdentifier),
      SK: this.getSK(safeIdentifier),
      SK2: this.getSK2(safePayload),
      payload: safePayload,
    };
  }

  private decodeUpdate(
    identifier: JsonIdentifier<Identifier>,
    update: JsonUpdate<Data>,
  ): {
    readonly PK: PK<EntityType>;
    readonly SK: SK;
    readonly safeUpdate: JsonUpdate<Data>;
  } {
    return {
      PK: this.getPK(identifier),
      SK: this.getSK(identifier),
      safeUpdate: this.updateSchema.decode(update),
    };
  }

  private async doPrepareUpdate(
    transaction: Transaction,
    identifier: JsonIdentifier<Identifier>,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<void> {
    const { PK, SK, safeUpdate } = this.decodeUpdate(identifier, update);
    await this.db.prepareUpdateById(transaction, PK, SK, safeUpdate, options);
  }

  private async doUpdate(
    identifier: JsonIdentifier<Identifier>,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<Payload> {
    const { PK, SK, safeUpdate } = this.decodeUpdate(identifier, update);
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    const updated = this.db.updateById(
      PK,
      SK,
      safeUpdate,
      options,
    ) as Promisable<Payload | null>;

    const payload = await updated;

    if (isNullish(payload)) {
      throw this.createNotFoundError(identifier);
    }

    return payload;
  }
}
